import { TableColumn } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { PromiseAtom } from '@/types/atom';
import { DataTableColumn } from '@/types/table';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { AGGREGATES_COLUMN_PREFIX, getMetricBaseName, hasMetricTranslation } from '@/utils/metrics';
import { objSnakeToCamel, objValsSnakeToCamel } from '@/utils/queries';
import { groupTableColumns } from '@/utils/table';
import { canUserSeeDevTeamFeatures } from '@/utils/user';
import { metricKeyIs360MapAtom, metricKeyIsPhysicalMapAtom } from './metric';
import { rawUserDetailsAtom } from './user';

export const tableColumnEntitiesAtom = atom<string[]>([]);

const rawTableColumnsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['table-columns', get(tableColumnEntitiesAtom)] as const;
  const queryFn = async ({ queryKey: [, tableColumnEntities] }: { queryKey: typeof queryKey }) => {
    const tableColumnParams = { in: { 'category_branch.key': tableColumnEntities } };
    if (hasEmptyInFilters(tableColumnParams)) return [];

    const columns = (await fetch(`/table-columns${convertFilterParamsToString(tableColumnParams)}`)) as TableColumn[];

    return columns.filter(column => {
      const { key } = column.table_column;
      if (!key.startsWith(AGGREGATES_COLUMN_PREFIX)) return true;
      return hasMetricTranslation(getMetricBaseName(key));
    });
  };
  return { queryKey, queryFn };
});

export const enrichedTableColumnsAtom = atom(async get => {
  const [rawTableColumns, metricKeyIs360Map, metricKeyIsPhysicalMap, userDetails] = await Promise.all([
    get(rawTableColumnsAtom),
    get(metricKeyIs360MapAtom),
    get(metricKeyIsPhysicalMapAtom),
    get(rawUserDetailsAtom),
  ]);

  const canSeePhysical = canUserSeeDevTeamFeatures(userDetails.data);

  return rawTableColumns.data
    .map(column => {
      const baseName = getMetricBaseName(column.table_column.key);
      return {
        ...column,
        is_360: metricKeyIs360Map[baseName] ?? false,
        is_physical: metricKeyIsPhysicalMap[baseName] ?? false,
      };
    })
    .filter(column => canSeePhysical || !column.is_physical);
});

export const tableColumnsAtom = atom(async (get): Promise<DataTableColumn[]> => {
  const enrichedTableColumns = await get(enrichedTableColumnsAtom);
  // objSnakeToCamel/objValsSnakeToCamel return wide NestedObject types; the return type annotation above enforces the shape
  return enrichedTableColumns.map(objSnakeToCamel).map(objValsSnakeToCamel) as DataTableColumn[];
});
export const unwrappedTableColumnsAtom = unwrap(tableColumnsAtom, prev => prev || []);

export const tableColumnGroupsAtom = atom(async get => {
  const tableColumns = await get(tableColumnsAtom);

  const tableColumnEntities = get(tableColumnEntitiesAtom);
  const hasPlayerMetric = tableColumnEntities.includes('player_metric');
  const hasPlayerTotalMetric = tableColumnEntities.includes('player_total_metric');
  const hasTeamMetric = tableColumnEntities.includes('team_metric');
  const hasTeamTotalMetric = tableColumnEntities.includes('team_total_metric');

  const filteredColumns = tableColumns.filter(column => {
    const key = column.categoryPath?.[0]?.key as string;
    if (hasPlayerMetric && hasPlayerTotalMetric && key === 'playerTotalMetric') return false;
    if (hasTeamMetric && hasTeamTotalMetric && key === 'teamTotalMetric') return false;
    return true;
  });

  return groupTableColumns(filteredColumns);
});

const fetchVisibleTableColumns = async (
  fetch: (url: string) => Promise<unknown>,
  tableColumnEntities: readonly string[],
): Promise<TableColumn[]> => {
  const tableColumnParams = { in: { 'category_branch.key': [...tableColumnEntities] } };
  const columns = (await fetch(`/table-columns${convertFilterParamsToString(tableColumnParams)}`)) as TableColumn[];
  return columns.filter(column => {
    const { key } = column.table_column;
    if (!key.startsWith(AGGREGATES_COLUMN_PREFIX)) return true;
    return hasMetricTranslation(getMetricBaseName(key));
  });
};

const makeRadarTableColumnsAtom = (entities: readonly string[]): PromiseAtom<DataTableColumn[]> => {
  const rawAtom = atomWithSuspenseQuery(get => {
    const { fetch } = get(fetchClientAtom);
    const queryKey = ['radar-table-columns', entities] as const;
    const queryFn = async () => fetchVisibleTableColumns(fetch, entities);
    return { queryKey, queryFn };
  });
  // objSnakeToCamel/objValsSnakeToCamel return wide NestedObject types; the return type annotation on the caller enforces the shape
  return atom(
    async get => (await get(rawAtom)).data.map(objSnakeToCamel).map(objValsSnakeToCamel) as DataTableColumn[],
  );
};

export const playerRadarTableColumnsAtom = makeRadarTableColumnsAtom(['player_metric']);
export const teamRadarTableColumnsAtom = makeRadarTableColumnsAtom(['team_metric']);

export const metricGroupsAtom = atom(async get => {
  const tableColumnGroups = await get(tableColumnGroupsAtom);

  // remove columns in the 'team' and 'player' category. An example of a column in the 'player' category is 'minutes played'.
  // Columns under these categories are not skill based metrics so are removed here.
  const metricGroups = tableColumnGroups.filter(
    group => group.header.translationKey !== 'team' && group.header.translationKey !== 'player',
  );
  return metricGroups;
});
